import { Box, Select, Option, Typography, Button, IconButton, Stack, Input } from "@mui/joy";
import { useEffect, useState, useRef } from "react";
import { debounce } from "lodash";
import { Gauge, Plus, X, Hash } from "lucide-react";

interface IFurnaceVentSize {
    value: string | null;
    onChange: (value: string) => void;
    loading?: boolean;
}

interface FurnaceVentEntry {
    size: string;
    count: number;
}

const SIZE_OPTIONS = ["4 inch", "5 inch", "6 inch", "7 inch", "TBD"];

/**
 * Repeatable Furnace Vent list.
 * Stores as JSON array: [{"size":"4 inch","count":1},{"size":"6 inch","count":2}]
 */
export function FurnaceVentSize(props: IFurnaceVentSize) {
    const { value, onChange, loading } = props;

    const parseValue = (val: string | null): FurnaceVentEntry[] => {
        if (!val) return [{ size: "4 inch", count: 1 }];
        try {
            const parsed = JSON.parse(val);
            if (Array.isArray(parsed) && parsed.length > 0 && "size" in parsed[0]) return parsed;
            // Legacy: plain string like "4 inch"
        } catch {}
        if (val && val.trim()) return [{ size: val.trim(), count: 1 }];
        return [{ size: "4 inch", count: 1 }];
    };

    const [entries, setEntries] = useState<FurnaceVentEntry[]>(() => parseValue(value));

    const debouncedOnChange = useRef(
        debounce((updated: FurnaceVentEntry[]) => {
            onChange(JSON.stringify(updated));
        }, 500)
    ).current;

    useEffect(() => {
        return () => { debouncedOnChange.cancel(); };
    }, [debouncedOnChange]);

    useEffect(() => {
        if (value) setEntries(parseValue(value));
    }, [value]);

    const handleUpdate = (index: number, field: keyof FurnaceVentEntry, val: any) => {
        if (loading) return;
        const updated = entries.map((e, i) => (i === index ? { ...e, [field]: val } : e));
        setEntries(updated);
        debouncedOnChange(updated);
    };

    const addEntry = () => {
        setEntries((prev) => [...prev, { size: "4 inch", count: 1 }]);
    };

    const removeEntry = (index: number) => {
        if (entries.length <= 1) {
            const reset = [{ size: "4 inch", count: 1 }];
            setEntries(reset);
            debouncedOnChange(reset);
            return;
        }
        const updated = entries.filter((_, i) => i !== index);
        setEntries(updated);
        debouncedOnChange(updated);
    };

    return (
        <Box flex={1}>
            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 1 }}>
                Furnace Vent / Cap / Collar
            </Typography>
            <Stack spacing={1}>
                {entries.map((entry, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                        {/* Size selector */}
                        <Select
                            size="sm"
                            value={entry.size}
                            onChange={(_e, val) => handleUpdate(i, "size", val)}
                            disabled={loading}
                            startDecorator={<Gauge size={14} />}
                            slotProps={{ listbox: { sx: { zIndex: 10001 } } }}
                            sx={{ flex: 1 }}
                        >
                            {SIZE_OPTIONS.map((opt) => (
                                <Option key={opt} value={opt}>{opt}</Option>
                            ))}
                        </Select>

                        {/* Count input */}
                        <Input
                            size="sm"
                            type="number"
                            placeholder="Qty"
                            value={entry.count}
                            onChange={(e) => handleUpdate(i, "count", Math.max(1, Number(e.target.value) || 1))}
                            disabled={loading}
                            startDecorator={<Hash size={14} />}
                            slotProps={{ input: { min: 1 } }}
                            sx={{ width: 80 }}
                        />

                        {/* Remove button */}
                        <IconButton
                            size="sm"
                            variant="soft"
                            color="danger"
                            onClick={() => removeEntry(i)}
                            sx={{ "--IconButton-size": "28px", border: "1px solid", borderColor: "divider" }}
                        >
                            <X size={14} />
                        </IconButton>
                    </Stack>
                ))}

                <Button
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    startDecorator={<Plus size={14} />}
                    onClick={addEntry}
                    disabled={loading}
                    sx={{ alignSelf: "flex-start", mt: "4px !important" }}
                >
                    Add Furnace Vent
                </Button>
            </Stack>
        </Box>
    );
}
